import { useCallback, type KeyboardEvent } from 'react';
import type { NoteItem } from '../../doc/schema';
import { useBoard, usePane, usePaneStore } from '../context';
import { textStyleCss, textStyleOf } from '../text/textStyle';

/** Grows the editor to its content so it can sit at the middle or bottom of the note like the text does. */
function fitEditor(el: HTMLTextAreaElement | null): void {
  if (!el) return;
  el.style.height = '0';
  el.style.height = `${el.scrollHeight}px`;
}

export function NoteBody({ item }: { item: NoteItem }) {
  const editing = usePane((s) => s.editingId === item.id);
  const pane = usePaneStore();
  const board = useBoard();

  const commit = useCallback(
    (text: string) => {
      if (text !== item.props.text) board.updateItem(item.id, { props: { text } });
      pane.getState().setEditing(null);
    },
    [board, item.id, item.props.text, pane],
  );

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    e.stopPropagation();
    if (e.key === 'Escape') e.currentTarget.blur();
  };

  // Sticky notes are light paper: default ink is dark regardless of theme.
  const textStyle = textStyleOf(item);
  const style = textStyleCss(textStyle, '#111111');
  const valign = textStyle.valign ?? 'top';

  if (editing) {
    return (
      <div className="note-editor-wrap" data-valign={valign}>
        <textarea
          ref={fitEditor}
          className="note-editor"
          style={style}
          defaultValue={item.props.text}
          autoFocus
          spellCheck={false}
          onPointerDown={(e) => e.stopPropagation()}
          onKeyDown={onKeyDown}
          onInput={(e) => fitEditor(e.currentTarget)}
          onBlur={(e) => commit(e.currentTarget.value)}
          onFocus={(e) => e.currentTarget.select()}
        />
      </div>
    );
  }

  return (
    <div className="note-text" style={style} data-valign={valign}>
      <div className="note-text-inner">{item.props.text || <span className="placeholder">Double-click to write</span>}</div>
    </div>
  );
}
